import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import * as permissions from '@/common/permissionConstants';
import CodeableConcept from '@/fhirworks/types/CodeableConcept';

// CONSTANTS
export const VERIFICATION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/condition-ver-status';
export const CLINICAL_SYSTEM = 'http://terminology.hl7.org/CodeSystem/condition-clinical';

export const CUSTOM_PROBLEM_STATUS_OPTIONS = [
    new CodeableConcept({ coding: [{ system: CLINICAL_SYSTEM, code: 'active', display: 'Active' }] }),
    new CodeableConcept({ coding: [{ system: CLINICAL_SYSTEM, code: 'recurrence', display: 'Recurrence' }] }),
    new CodeableConcept({ coding: [{ system: CLINICAL_SYSTEM, code: 'relapse', display: 'Relapse' }] }),
    new CodeableConcept({ coding: [{ system: CLINICAL_SYSTEM, code: 'remission', display: 'Remission' }] }),
    new CodeableConcept({ coding: [{ system: CLINICAL_SYSTEM, code: 'resolved', display: 'Resolved' }] }),
    new CodeableConcept({ coding: [{ system: VERIFICATION_SYSTEM, code: 'entered-in-error', display: 'Entered in error' }] }),
];

export const LIFECYCLE_STATUS_OPTIONS = [
    { code: 'proposed', display: 'Proposed' },
    { code: 'active', display: 'Active' },
    { code: 'cancelled', display: 'Cancelled' },
    { code: 'completed', display: 'Completed' },
    { code: 'entered-in-error', display: 'Entered in error' },
];

export const CARE_PLAN_STATUS_OPTIONS = [
    {
        code: 'draft',
        display: 'Proposed',
        definition: 'The request has been created but is not yet complete or ready for action.',
    },
    {
        code: 'active',
        display: 'Active',
        definition: 'The request is in force and ready to be acted upon.',
    },
    {
        code: 'revoked',
        display: 'Cancelled',
        definition: 'The request (and any implicit authorization to act) has been terminated prior to the known full completion of the intended actions. No further activity should occur.',
    },
    {
        code: 'completed',
        display: 'Completed',
        definition: 'The activity described by the request has been fully performed. No further activity will occur.',
    },
    {
        code: 'entered-in-error',
        display: 'Entered in error',
        definition: 'The request was entered in error and should not be acted upon.',
    },
];

export const INTERVENTION_STATUS_OPTIONS = [
    { code: 'not-started', display: 'Proposed' },
    { code: 'in-progress', display: 'Active' },
    { code: 'cancelled', display: 'Cancelled' },
    { code: 'completed', display: 'Completed' },
    { code: 'entered-in-error', display: 'Entered in error' },
];

export const THERAPEUTIC_INTERVENTION_MODALITIES = [
    { title: 'Practice Standards', value: 'ps' },
    { title: 'Humanistic/Existential Theoretical', value: 'huma' },
    { title: 'Psychodynamic Therapy', value: 'ptm' },
    { title: 'Behavior Therapy', value: 'btm' },
    { title: 'Cognitive-Behavioral Therapy', value: 'cbtm' },
    { title: 'Dialectical Behavioral Therapy', value: 'dial' },
    { title: 'Acceptance and Commitment Therapy', value: 'actm' },
    { title: 'Family Therapy', value: 'ftm' },
    { title: 'Group Therapy', value: 'gtm' },
    { title: 'Motivational Interviewing', value: 'mim' },
    { title: 'Interpersonal and Social Rhythm Therapy', value: 'isrtm' },
    { title: 'Brief Cognitive Behavioral Therapy for Suicide Prevention', value: 'bcbtsm' },
    { title: 'Harm Reduction', value: 'harm' },
    { title: 'Matrix Model', value: 'matr' },
    { title: 'Twelve Step Facilitation', value: 'twel' },
    { title: 'Cognitive Processing Therapy', value: 'cptm' },
    { title: 'Eye Movement Desensitization and Reprocessing', value: 'emdrm' },
    { title: 'Somatic Experiencing', value: 'soma' },
    { title: 'Behavioral Parent Training', value: 'beha' },
    { title: 'Child-Centered Play Therapy', value: 'chil' },
    { title: 'School-Based Behavioral Management', value: 'scho' },
    { title: 'Trauma-Focused Cognitive Behavioral Therapy', value: 'tfcb' },
];

export const THERAPEUTIC_INTERVENTION_LEVEL_MAP = [
    { level: 1, icon: 'dial-max', color: 'info' },
    { level: 2, icon: 'dial', color: 'inProgress' },
    { level: 3, icon: 'dial-med-low', color: 'warning' },
    { level: 4, icon: 'dial-min', color: 'error' },
];

// COMPOSABLE FUNCTION

export function useTreatmentPlanUtils() {
    const authStore = useAuthStore();
    // PERMISSION FUNCTIONS
    const viewOnlyPermission = computed(() => !authStore.hasPermission(permissions.MANAGE_PROBLEM_TREATMENT_PLAN));

    const canViewPermission = computed(() => authStore.hasPermission(permissions.VIEW_PROBLEM_TREATMENT_PLAN) || authStore.hasPermission(permissions.MANAGE_PROBLEM_TREATMENT_PLAN));

    // STATUS HANDLING FUNCTIONS
    const getStatusColor = (val) => {
        if (!val) return;
        let status = val?.coding?.[0]?.code || val;
        if (['provisional', 'draft', 'proposed'].includes(status)) return 'warning';
        if (['completed', 'resolved'].includes(status)) return 'primary';
        if (['recurrence', 'accepted', 'relapse', 'active', 'remission'].includes(status)) return 'success';
        if (['cancelled', 'entered-in-error', 'canceled', 'revoked'].includes(status)) return 'red';
        if (['provisional', 'unknown'].includes(status?.verificationStatus?.coding?.[0].code) || ['provisional', 'unknown'].includes(status?.clinicalStatus?.coding?.[0].code)) return 'warning';
        if (['completed', 'resolved'].includes(val?.verificationStatus?.coding?.[0].code) || ['completed', 'resolved'].includes(val?.clinicalStatus?.coding?.[0].code)) return 'primary';

        if (
            ['recurrence', 'accepted', 'relapse', 'active', 'remission'].includes(val?.verificationStatus?.coding?.[0].code) ||
            ['recurrence', 'accepted', 'relapse', 'active', 'remission'].includes(val?.clinicalStatus?.coding?.[0].code)
        )
            return 'success';
        if (
            ['cancelled', 'canceled', 'entered-in-error', 'revoked'].includes(val?.verificationStatus?.coding?.[0].code) ||
            ['cancelled', 'canceled', 'entered-in-error', 'revoked'].includes(val?.clinicalStatus?.coding?.[0].code)
        )
            return 'red';
        return 'success';
    };

    const setInitialProblemStatus = (val, verificationStatus, clinicalStatus) => {
        if (val.coding[0].system === VERIFICATION_SYSTEM) {
            verificationStatus.value = val;
            // For entered-in-error, unset clinical status per FHIR guidelines
            if (val.coding[0].code === 'entered-in-error') {
                clinicalStatus.value = undefined;
            } else {
                clinicalStatus.value = CUSTOM_PROBLEM_STATUS_OPTIONS.find((el) => el.coding[0].code === 'active');
            }
            return;
        }
        verificationStatus.value = undefined;
        clinicalStatus.value = val;
    };

    const getInterventionStatusDisplay = (status) => {
        const statusObj = INTERVENTION_STATUS_OPTIONS.find((s) => s.code === status);
        return statusObj ? statusObj.display : status || 'Not started';
    };

    const getInterventionStatusColor = (status) => {
        if (['completed'].includes(status)) return 'primary';
        if (['cancelled', 'stopped', 'entered-in-error'].includes(status)) return 'red';
        if (['in-progress'].includes(status)) return 'success';
        if (['on-hold'].includes(status)) return 'warning';
        if (['scheduled'].includes(status)) return 'info';
        if (['not-started'].includes(status)) return 'warning';
        return 'grey';
    };

    // UTILITY FUNCTIONS
    const getPerformerCredentials = (performer, practitionerData) => {
        if (!performer.id) return '';

        const fullPractitioner = practitionerData?.find((p) => p.id === performer.id);
        return fullPractitioner?.credentials || '';
    };

    const getPractitionersFromDepartments = (departments, departmentData, practitionerData) => {
        const practitionerMap = new Map();

        // Add practitioners from departments if available
        if (departments?.length && departmentData?.length) {
            departments.forEach((deptRef) => {
                const department = departmentData.find((d) => d.id === deptRef.id);
                department?.practitioner?.forEach((practitionerRef) => {
                    if (practitionerRef.id && !practitionerMap.has(practitionerRef.id)) {
                        // Find the full practitioner data
                        const fullPractitioner = practitionerData.find((p) => p.id === practitionerRef.id);
                        if (fullPractitioner) {
                            const practitionerObj = {
                                id: practitionerRef.id,
                                display: fullPractitioner?.nameDisplay,
                                credentials: fullPractitioner?.credentials,
                                resourceType: 'Practitioner',
                            };
                            practitionerMap.set(practitionerRef.id, practitionerObj);
                        }
                    }
                });
            });
        }

        // Add the current user as an option (when no departments or data are present)
        if (authStore.practitioner?.id && !practitionerMap.has(authStore.practitioner.id)) {
            const currentUserObj = {
                id: authStore.practitioner.id,
                display: authStore.practitioner.nameDisplay || 'Current User',
                credentials: authStore.practitioner.credentials,
                resourceType: 'Practitioner',
            };
            practitionerMap.set(authStore.practitioner.id, currentUserObj);
        }

        return Array.from(practitionerMap.values()).sort((a, b) => (a.display || '').localeCompare(b.display || ''));
    };

    const getInterventionNumber = (intervention, activities) => {
        if (!intervention || !activities) return 0;

        // Find the index of this intervention in the activities array
        const index = activities.findIndex((act) => act === intervention);
        return index + 1;
    };

    const getInterventionDisplay = (intervention) => {
        if (!intervention) return;
        return intervention?.detail?.code?.coding?.[0]?.display;
    };

    const getLevelChipColor = (level) => {
        if (level === undefined || level === null) return;
        // Level 0 gets grey color (for "No level")
        if (level === 0) return 'grey';
        // Get color for the specific level value
        return THERAPEUTIC_INTERVENTION_LEVEL_MAP.find((el) => el.level === level)?.color;
    };

    const formatLevelDisplay = (level) => {
        if (!Array.isArray(level) || level.length === 0) return '';
        // Filter out 0 and sort, then format
        const validLevels = level.filter((l) => l !== 0 && l !== null && l !== undefined).sort((a, b) => a - b);
        if (validLevels.length === 0) return 'No level';
        return `Level ${validLevels.join(', ')}`;
    };

    const hasValidLevel = (level) => {
        if (!Array.isArray(level) || level.length === 0) return false;
        return level.some((l) => l !== 0 && l !== null && l !== undefined);
    };

    const getLevelsArray = (level) => {
        if (!Array.isArray(level) || level.length === 0) return [];
        // Keep 0 if there are other levels, filter out null/undefined and sort
        const hasNonZeroLevels = level.some((l) => l !== 0 && l !== null && l !== undefined);
        if (hasNonZeroLevels) {
            // Include 0 when there are other levels, filter out null/undefined and sort
            return level.filter((l) => l !== null && l !== undefined).sort((a, b) => a - b);
        } else {
            // If only 0 or invalid levels, filter out 0, null, undefined (will show "No level" chip instead)
            return level.filter((l) => l !== 0 && l !== null && l !== undefined).sort((a, b) => a - b);
        }
    };

    const getTherapeuticInterventionTitle = (value) => {
        if (!value) return 'All objectives';
        const modality = THERAPEUTIC_INTERVENTION_MODALITIES.find((m) => m.value === value);
        return modality ? modality.title : value;
    };

    const getTherapeuticInterventionLevel = (category, interventionValue) => {
        if (!category?.modality) return null;
        return category.modality.find((e) => e.modal === interventionValue);
    };

    // DIAGNOSIS HANDLING FUNCTIONS
    const getStatusText = (val) => {
        if (val.verificationStatus) return val.verificationStatus?.coding?.[0].display;
        if (val.clinicalStatus) return val.clinicalStatus?.coding?.[0].display;
    };

    const getSelectedDxs = (problem, chipDx) => {
        const selectedDxs = [];
        if (problem.basedOn && Array.isArray(problem.basedOn)) {
            problem.basedOn.forEach((basedOn) => {
                if (basedOn.identifier?.type?.coding?.[0]?.code) {
                    const matchingDx = chipDx.find((dx) => dx.code.coding[0].code === basedOn.identifier.type.coding[0].code);
                    if (matchingDx) {
                        selectedDxs.push(matchingDx);
                    }
                }
            });
        }
        return selectedDxs;
    };

    const isDxSelected = (dx, problem, chipDx) => {
        const selectedDxs = getSelectedDxs(problem, chipDx);
        return selectedDxs.some((selectedDx) => selectedDx.code.coding[0].code === dx.code.coding[0].code);
    };

    const toggleDxSelection = (dx, selectedDxs) => {
        const isSelected = selectedDxs.some((selected) => selected.code.coding[0].code === dx.code.coding[0].code);

        if (isSelected) {
            return selectedDxs.filter((selected) => selected.code.coding[0].code !== dx.code.coding[0].code);
        } else {
            return [...selectedDxs, dx];
        }
    };

    // CONTENT FILTER TEXT FUNCTIONS
    const getContentFilterText = (hasExistingContent) => (hasExistingContent ? 'Edit content in session' : 'Add content to session');

    return {
        viewOnlyPermission,
        canViewPermission,

        getStatusColor,
        setInitialProblemStatus,
        getInterventionStatusDisplay,
        getInterventionStatusColor,
        getStatusText,

        getPerformerCredentials,
        getPractitionersFromDepartments,
        getInterventionNumber,
        getInterventionDisplay,

        getLevelChipColor,
        formatLevelDisplay,
        hasValidLevel,
        getLevelsArray,
        getTherapeuticInterventionTitle,
        getTherapeuticInterventionLevel,

        getSelectedDxs,
        isDxSelected,
        toggleDxSelection,

        getContentFilterText,
    };
}
